// i18n ignore
/* eslint-disable @typescript-eslint/no-require-imports */
import { once } from 'lodash';
import { getTestBaseUrl, getSearchDevOpen } from './useGogo';
import { managerApiUrl, managerAuthHeaders, resolveCreateWujiHost } from './useGogoRemoteContainer';
const GOGO_BASE_HOST = 'https://wujipt.woa.com';
const GOGO_BASE_PATH = '/api/gogo';
const CSS_TIMEOUT = 15_000;
const JS_TIMEOUT = 30_000;
const MAX_RETRIES = 3;
// ---------------------------------------------------------------------------
// 第一层 – 底层 DOM 加载器
// ---------------------------------------------------------------------------
function loadStylesheet(href, timeout = CSS_TIMEOUT) {
    return new Promise((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.href = href;
        link.setAttribute('data-wuji-gogo-editor', 'css');
        const settle = once((ok) => {
            clearTimeout(timer);
            ok ? resolve(link) : reject(new Error(`Stylesheet load failed: ${href}`));
        });
        const timer = setTimeout(() => settle(false), timeout);
        link.onload = () => settle(true);
        link.onerror = () => settle(false);
        document.head.appendChild(link);
    });
}
function loadScript(src, timeout = JS_TIMEOUT) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = src;
        script.setAttribute('data-wuji-gogo-editor', 'js');
        script.crossOrigin = 'anonymous';
        const settle = once((ok) => {
            clearTimeout(timer);
            ok ? resolve(script) : reject(new Error(`Script load failed: ${src}`));
        });
        const timer = setTimeout(() => settle(false), timeout);
        script.onload = () => settle(true);
        script.onerror = () => settle(false);
        document.head.appendChild(script);
    });
}
function removeEditorAssets() {
    try {
        document.querySelectorAll('[data-wuji-gogo-editor]').forEach(el => el.remove());
    }
    catch {
        /* ignore */
    }
}
// ---------------------------------------------------------------------------
// 第二层 – 单 URL 资源加载（含清理）
// ---------------------------------------------------------------------------
async function loadAssetsFromUrl(url, resolveEditor) {
    removeEditorAssets();
    try {
        await Promise.all([
            loadStylesheet(`${url}/for-cdn.css`),
            loadScript(`${url}/for-cdn.js`),
        ]);
        return await resolveEditor();
    }
    catch (error) {
        removeEditorAssets();
        throw error;
    }
}
// ---------------------------------------------------------------------------
// 第三层 – 重试包装器
// ---------------------------------------------------------------------------
async function withRetries(fn, label, retries = MAX_RETRIES) {
    let lastError;
    for (let attempt = 1; attempt <= retries + 1; attempt++) {
        try {
            return await fn();
        }
        catch (e) {
            lastError = e;
            console.warn(`[editor-cdn] ${label} attempt ${attempt}/${retries + 1} failed`, e);
        }
    }
    throw lastError;
}
// ---------------------------------------------------------------------------
// 第四层 – Manifest URL 解析
// ---------------------------------------------------------------------------
async function fetchManifestCdnUrl() {
    try {
        const status = getSearchDevOpen() ? 'test' : 'prod';
        const runtimeVersion = require('@tencent/wuji-gogo-runtime/package.json')?.version || '';
        const major = runtimeVersion.split('.')[0];
        const host = window.GOGO_CONFIG?.baseUrl || GOGO_BASE_HOST;
        const path = window.GOGO_CONFIG?.basePath || GOGO_BASE_PATH;
        const res = await fetch(`${host}${path}/manifest?status=${status}&major=${major}`);
        const data = await res.json();
        const candidates = Array.isArray(data.data) ? data.data : [];
        return candidates.find(x => x?.baseUrl)?.baseUrl || null;
    }
    catch (error) {
        console.error('[editor-cdn] Failed to fetch manifest', error);
        return null;
    }
}
/**
 * 拉取 editor-remote 滚动发布地址：走 manager /container/editor-remote-url。
 * 有 MANAGER_TOKEN 时改走 /open/api/container/editor-remote-url + x-wuji-token。
 * manager 内部按 (runtime-remote major + 当前/期望容器 devenv major) 命中 status=prod 最新 productVersion。
 * projectId/branchId/wuji_host 与建容器落库口径一致。
 * wuji_host 走 resolveCreateWujiHost（含 getTarUrl 覆盖），与 /container/new 同一套。
 */
export async function fetchEditorRemoteCdnUrl() {
    try {
        const search = new URLSearchParams(window.location.search);
        const projectId = search.get('projectid') || '';
        const branchId = search.get('branch') || '';
        const runtimeVersion = require('@tencent/wuji-gogo-runtime-remote/package.json')?.version || '';
        if (!projectId || !runtimeVersion)
            return null;
        const status = getSearchDevOpen() ? 'test' : 'prod';
        const { wujiHost } = await resolveCreateWujiHost();
        const params = new URLSearchParams({ projectId, runtimeVersion, status });
        if (wujiHost)
            params.set('wuji_host', wujiHost);
        if (branchId)
            params.set('branchId', branchId);
        const res = await fetch(`${managerApiUrl('/container/editor-remote-url')}?${params.toString()}`, {
            credentials: 'include',
            headers: { Accept: 'application/json', ...managerAuthHeaders() },
        });
        if (!res.ok)
            return null;
        const data = await res.json();
        return data?.url || null;
    }
    catch (error) {
        console.error('[editor-remote-cdn] Failed to fetch url', error);
        return null;
    }
}
// ---------------------------------------------------------------------------
// 第五层 – 公开 API
// ---------------------------------------------------------------------------
/**
 * 加载编辑器 CDN 资源，采用三级降级策略，每级失败后重试 {@link MAX_RETRIES} 次再降级。
 * 每次资源加载后都会执行 resolveEditor，确保脚本真正完成编辑器注册。
 *
 *  1. testBaseUrl  – URL 查询参数指定的测试地址
 *  2. manifest CDN – 从 manifest 接口获取的滚动发布地址
 *  3. baseUrl      – 随构建打包的静态文件 CDN（兜底）
 */
export async function loadEditorCdn(baseUrl, resolveEditor, options) {
    // 1. 测试地址（URL 查询参数）—— 指定了就只用它，失败直接抛出
    const testUrl = getTestBaseUrl();
    if (testUrl) {
        return withRetries(() => loadAssetsFromUrl(testUrl, resolveEditor), `testBaseUrl(${testUrl})`);
    }
    // 2. 滚动发布（manifest 接口）；不同编辑器可传各自的地址解析器（editor / editor-remote）
    if (!XY_USE_LOCAL_GOGO) {
        const fetchManifestUrl = options?.fetchManifestUrl ?? fetchManifestCdnUrl;
        const manifestUrl = await fetchManifestUrl();
        if (manifestUrl) {
            try {
                return await withRetries(() => loadAssetsFromUrl(manifestUrl, resolveEditor), `manifest(${manifestUrl})`);
            }
            catch (e) {
                console.error('[editor-cdn] manifest 地址全部重试失败，尝试下一来源', e);
            }
        }
    }
    // 3. 默认静态文件 CDN（兜底，失败则抛出异常）
    return withRetries(() => loadAssetsFromUrl(baseUrl, resolveEditor), `baseUrl(${baseUrl})`);
}
